import { MapPin, Mail, Phone, ArrowRight } from "lucide-react";
import { Container, Eyebrow } from "@/components/common/Section";
import { Reveal } from "@/lib/anim";

const MAILTO = "mailto:info@ijeeva.com?subject=Conversation%20with%20iJeeva";

const iconBox =
  "flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] border border-[#E2E8F0] bg-white text-[#2563EB]";

export default function Contact() {
  return (
    <section data-testid="contact-page" className="relative overflow-hidden">
      <div
        className="bg-grid absolute inset-0 [mask-image:radial-gradient(ellipse_75%_60%_at_50%_0%,black,transparent)]"
        aria-hidden="true"
      />
      <Container className="relative grid gap-14 py-16 lg:grid-cols-2 lg:gap-20 lg:py-24">
        <Reveal>
          <Eyebrow>Contact</Eyebrow>
          <h1
            data-testid="contact-page-title"
            className="mt-4 text-4xl font-semibold leading-[1.06] tracking-tight text-[#1D305B] sm:text-5xl lg:text-[56px]"
          >
            Let's build what's next.
          </h1>
          <p className="mt-6 max-w-md text-base leading-relaxed text-[#64748B] sm:text-lg">
            Tell us what you're building, modernizing or connecting. Our payments and technology team will get in
            touch.
          </p>
          <div className="mt-9">
            <a
              href={MAILTO}
              data-testid="contact-email-cta"
              className="btn-brand group inline-flex items-center gap-2.5 whitespace-nowrap rounded-[10px] px-7 py-4 text-[15px] font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_16px_36px_-12px_rgba(37,99,235,0.55)]"
            >
              Start a Conversation
              <ArrowRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
            </a>
            <p className="mt-4 text-sm text-[#94A3B8]">Opens your email client, addressed to info@ijeeva.com.</p>
          </div>

          <div className="mt-10 space-y-4">
            <div data-testid="contact-address" className="flex items-start gap-3">
              <span className={iconBox}>
                <MapPin className="h-4 w-4" />
              </span>
              <p className="pt-1.5 text-[15px] leading-relaxed text-[#475569]">
                Abhaya Heights, 7th Floor, No.11, Service Road, J P Nagar 3rd Phase, Bannerghatta Road, Bangalore,
                Karnataka, India - 560078
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className={iconBox}>
                <Mail className="h-4 w-4" />
              </span>
              <a
                href="mailto:info@ijeeva.com"
                data-testid="contact-email"
                className="text-[15px] font-medium text-[#1D305B] transition-colors hover:text-[#2563EB]"
              >
                info@ijeeva.com
              </a>
            </div>
            <div className="flex items-center gap-3">
              <span className={iconBox}>
                <Phone className="h-4 w-4" />
              </span>
              <a
                href="tel:+919538547949"
                data-testid="contact-phone"
                className="text-[15px] font-medium text-[#1D305B] transition-colors hover:text-[#2563EB]"
              >
                +91-9538547949
              </a>
            </div>
          </div>
        </Reveal>

        <Reveal delay={140}>
          <div className="space-y-6">
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04)] sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2563EB]">What happens next</p>
              <ol className="mt-5 space-y-4">
                {[
                  "We review your requirements and context.",
                  "A payments specialist, not a sales queue, responds.",
                  "We set up a working session on architecture, platforms or integrations.",
                ].map((s, i) => (
                  <li key={s} className="flex gap-3.5">
                    <span className="font-mono-brand text-sm font-semibold text-[#0EA5A4]">{i + 1}</span>
                    <span className="text-[15px] leading-relaxed text-[#475569]">{s}</span>
                  </li>
                ))}
              </ol>
            </div>
            <div className="rounded-2xl border border-[#E2E8F0] bg-white p-7 shadow-[0_1px_2px_rgba(15,23,42,0.04)] sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-[#2563EB]">Helpful to include</p>
              <ul className="mt-5 space-y-3">
                {[
                  "What you're building, modernizing or connecting",
                  "Platforms or rails in scope (cards, wallets, banks, FX)",
                  "Rough timelines and the team involved",
                ].map((s) => (
                  <li key={s} className="flex items-start gap-3 text-[15px] leading-relaxed text-[#475569]">
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#0EA5A4]" aria-hidden="true" />
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </Container>
    </section>
  );
}
